<template>
  <div class="pb-4">
    <header class="card__container">
      <!-- Customer Detail Start -->
      <div class="header__container">
        <div class="d-flex justify-content-between align-items-start">
          <label class="text__header"
            >Site Access ID : {{ siteAccessId }}</label
          >
          <b-badge pill :variant="badgeVariant" class="badge--style tag--space">
            {{ badgeStatus }}
          </b-badge>
        </div>

        <div class="d-md-flex mt-1 mt-md-0 flex-wrap">
          <label class="text__customer-detail_topic">Customer Name</label>
          <label class="text__customer-detail_data ml-md-2">{{
            customerDetail.name
          }}</label>
        </div>
        <div class="d-md-flex mt-1 mt-md-0 flex-wrap">
          <label class="text__customer-detail_topic">{{
            contractIdLabel
          }}</label>
          <label class="text__customer-detail_data ml-md-2">{{
            customerDetail.idcId
          }}</label>
        </div>
        <div class="d-md-flex mt-1 mt-md-0 flex-wrap">
          <label class="text__customer-detail_topic">NTDC Site</label>
          <label class="text__customer-detail_data ml-md-2">{{
            customerDetail.siteName
          }}</label>
        </div>
      </div>
      <!-- Customer Detail End -->

      <hr />

      <!-- QR Access Detail Start -->
      <div class="header__container">
        <div v-if="isEditDetail">
          <div class="d-flex flex-column col-12 col-lg-9 px-0">
            <div class="d-lg-flex w-100">
              <div class="col-12 col-lg-6 px-0 pr-lg-1">
                <atom-datepicker
                  datepickerId="request_start_date"
                  title="Request Start Date"
                  :isAsterisk="true"
                  v-model="requestDetail.startDate"
                  placeholder="Request Start Date"
                  :minDate="minSiteAccessRequestStartDate"
                  :maxDate="requestDetail.endDate"
                  type="dateNtime"
                  :isError="validateRequestDetailError.startDate"
                  errorMessage="Please enter start date"
                  :showSetTimeNowButton="true"
                ></atom-datepicker>
              </div>
              <div class="col-12 col-lg-6 px-0 mt-1 mt-lg-0 pl-lg-1">
                <atom-datepicker
                  datepickerId="request_end_date"
                  title="Request End Date"
                  :isAsterisk="true"
                  v-model="requestDetail.endDate"
                  placeholder="Request End Date"
                  :minDate="requestDetail.startDate"
                  :maxDate="maxSiteAccessRequestEndDate"
                  type="dateNtime"
                  :isError="validateRequestDetailError.endDate"
                  errorMessage="Please enter end date"
                  :showSetTimeNowButton="true"
                ></atom-datepicker>
              </div>
            </div>

            <atom-text-area
              v-model="requestDetail.description"
              title="Description"
              placeholder="Description"
              :isAsterisk="true"
              :isError="validateRequestDetailError.description"
              errorMessage="Please enter description"
              class="mt-1"
            ></atom-text-area>
          </div>

          <div class="mt-2 col-12 col-lg-9 px-0">
            <b-alert :show="true" variant="secondary" class="w-100">
              <div class="alert-body">
                <strong>Please Note:</strong> Please specify the entry and
                exit date/time to cover your entire visit. <br />Access is
                not permitted before the Start Date or after the End Date.
                <br />For visits longer than one day, you can specify a
                date range in a single Site Access request.
              </div>
            </b-alert>
          </div>

          <div class="d-flex justify-content-end mt-2">
            <atom-button
              buttonId="btn-save-detail"
              @click="onClickSaveDetail"
              :isDisable="isRequestDetailError"
              >Save Changes</atom-button
            >
            <atom-button
              buttonId="btn-cancel-save-detail"
              buttonStyle="border"
              buttonColor="secondary"
              @click="onClickCancelSaveDetail"
              class="ml-12px"
              >Cancel</atom-button
            >
          </div>
        </div>

        <div v-else class="d-flex justify-content-between align-items-start">
          <div class="d-flex flex-column col-lg-9 px-0">
            <div class="d-lg-flex">
              <div class="col-12 col-lg-6 px-0 pr-lg-1 d-md-flex">
                <div class="w-150px text__normal">Request Start Date:</div>
                <span class="text__medium">{{ startDate }}</span>
              </div>
              <div class="col-12 col-lg-6 px-0 mt-1 mt-lg-0 pl-lg-1 d-md-flex">
                <div class="w-150px text__normal">Request End Date:</div>
                <span class="text__medium">{{ endDate }}</span>
              </div>
            </div>
            <div class="mt-1 d-md-flex">
              <div class="w-150px text__normal">Description:</div>
              <span class="text__medium w-des text-show-new-line">{{
                requestDetail.description
              }}</span>
            </div>
          </div>

          <atom-button
            v-if="isEditRequest && hasUpdateSiteAccessPermission"
            buttonId="btn-edit-request"
            iconLeft="EditIcon"
            buttonStyle="flat"
            buttonColor="secondary"
            class="btn-edit-request"
            @click="isEditDetail = !isEditDetail"
          ></atom-button>
        </div>

        <div v-if="!isEditDetail" class="mt-2 col-12 col-lg-10 col-xl-8 px-0">
          <b-alert :show="true" variant="secondary" class="w-100">
            <div class="alert-body">
              <strong>Please Note:</strong> Please specify the entry and
              exit date/time to cover your entire visit. <br />Access is
              not permitted before the Start Date or after the End Date.
              <br />For visits longer than one day, you can specify a date
              range in a single Site Access request.
            </div>
          </b-alert>
        </div>

      </div>
      <!-- QR Access Detail End -->
    </header>

    <!-- Visitor Detail Start -->
    <molecule-visitor-card-group
      :idTypeOptions="idTypeOptions"
      :requestStatus="siteAccessStatus"
      :visitorListProp="visitorList"
      :validateVisitorError="validateRequestDetailError.visitor"
      :visitorOptions="visitorOptions"
      :scrollToVisitorId="scrollToVisitorId"
      :hasUpdatePermission="hasUpdateSiteAccessPermission"
      @onResendEmail="onResendEmail"
      @clickCopyVisitorListCreateNewSiteAccess="
        onClickCopyVisitorListCreateNewSiteAccess
      "
      @updateVisitorList="onUpdateVisitorList"
      class="mt-30px"
    />
    <!-- Visitor Detail End -->

    <!-- Attachment Start -->
    <div class="mt-30px">
      <molecule-attachment-list
        :attachmentList="attachmentListExternal"
        modalAttachmentId="modal-attachment-external"
        :requestStatus="siteAccessStatus"
        title="Customer Attachment"
        :tagList="tagList"
        :hasEditPermission="hasUpdateSiteAccessPermission"
        @onRequestAddAttachment="onRequestAddAttachment"
        @onRequestEditAttachment="onRequestEditAttachment"
        @onRequestDeleteAttachment="onRequestDeleteAttachment"
      ></molecule-attachment-list>
      <molecule-attachment-list
        :attachmentList="attachmentListInternal"
        modalAttachmentId="modal-attachment-internal"
        :requestStatus="siteAccessStatus"
        :tagList="tagList"
        :hasEditPermission="hasUpdateSiteAccessPermission"
        @onRequestAddAttachment="onRequestAddAttachment"
        @onRequestEditAttachment="onRequestEditAttachment"
        @onRequestDeleteAttachment="onRequestDeleteAttachment"
      ></molecule-attachment-list>
    </div>
    <!-- Attachment End -->

    <div
      v-if="hasUpdateSiteAccessPermission"
      class="d-flex mt-30px"
      :class="
        hasDeleteSiteAccessPermission
          ? 'justify-content-between'
          : 'justify-content-end'
      "
    >
      <!-- Button Delete Request Start -->
      <atom-button
        v-if="
          (isWaiting || (isInternalAccess && isRejected)) &&
          hasDeleteSiteAccessPermission
        "
        buttonId="btn-delete"
        buttonStyle="flat"
        buttonColor="danger"
        iconLeft="Trash2Icon"
        @click="onClickDeleteRequest"
        >Delete</atom-button
      >
      <!-- Button Delete Request End -->

      <div
        v-if="isInternalAccess && isWaiting"
        class="d-flex justify-content-between"
      >
        <!-- Button Approve Request Start -->
        <atom-button
          buttonId="btn-approve"
          buttonColor="success"
          @click="openModal('modal-confirm-approve-site-access')"
          ><span class="d-lg-none">Approve</span
          ><span class="d-none d-lg-block"
            >Approve Site Access</span
          ></atom-button
        >
        <!-- Button Approve Request End -->

        <!-- Button Reject Request Start -->
        <atom-button
          buttonId="btn-reject"
          buttonStyle="border"
          buttonColor="danger"
          class="ml-1"
          @click="openModal('modal-confirm-reject-site-access')"
          >Reject</atom-button
        >
        <!-- Button Reject Request End -->
      </div>

      <div
        v-if="isInternalAccess && (isApproved || isAccess) && isCanCloseAccess"
        class="w-100 d-flex justify-content-end"
      >
        <!-- Button Close Access End -->
        <atom-button
          buttonId="btn-close"
          buttonStyle="border"
          buttonColor="secondary"
          @click="openModal('modal-confirm-close-site-access')"
          >Close Site Access
        </atom-button>
        <!-- Button Close Access End -->
      </div>

      <div
        v-if="isInternalAccess && isClosed && isCanReAccess"
        class="w-100 d-flex justify-content-end"
      >
        <!-- Button Re-Access End -->
        <atom-button
          buttonId="btn-re-access"
          buttonStyle="border"
          @click="onClickUpdateStatusRequest('ACCESS')"
          >Re Access</atom-button
        >
        <!-- Button Re-Access End -->
      </div>
    </div>

    <!-- Modal Start -->
    <div>
      <!-- start modal delete access-->
      <molecule-modal-dialog
        modalId="modal-confirm-delete-site-access"
        icon="AlertCircleIcon"
        textTitle="Do you want to delete?"
        :textSubtitle="`Site Access ID: ${this.siteAccessId}`"
        buttonTextLeft="Delete"
        buttonTextRight="Cancel"
        buttonColorLeft="danger"
        :isTextCenter="true"
        @clickButtonLeft="onClickConfirmDeleteRequest"
      >
      </molecule-modal-dialog>
      <!-- end modal delete access -->

      <!-- start modal approve access-->
      <molecule-modal-dialog
        modalId="modal-confirm-approve-site-access"
        textTitle="Do you want to approve?"
        :textSubtitle="`Site Access ID: ${this.siteAccessId}`"
        buttonTextLeft="Approve"
        buttonTextRight="Cancel"
        buttonColorLeft="success"
        :isTextCenter="true"
        @clickButtonLeft="onClickUpdateStatusRequest('APPROVED')"
      >
      </molecule-modal-dialog>
      <!-- end modal approve access -->

      <!-- start modal reject access-->
      <molecule-modal-dialog
        modalId="modal-confirm-reject-site-access"
        textTitle="Do you want to reject?"
        :textSubtitle="`Site Access ID: ${this.siteAccessId}`"
        buttonTextLeft="Reject"
        buttonTextRight="Cancel"
        buttonColorLeft="danger"
        :isTextCenter="true"
        :isSlotBody="true"
        :isDisableButtonLeft="!rejectReason"
        @clickButtonLeft="onClickUpdateStatusRequest('REJECTED')"
        ><template #body>
          <div class="w-100 px-lg-4">
            <atom-input
              v-model="rejectReason"
              title="Remark"
              placeholder="Remark"
              :isAsterisk="true"
              class="w-100"
            />
          </div>
        </template>
      </molecule-modal-dialog>
      <!-- end modal reject access -->

      <!-- start modal close access-->
      <molecule-modal-dialog
        modalId="modal-confirm-close-site-access"
        textTitle="Do you want to close?"
        :textSubtitle="`Site Access ID: ${this.siteAccessId}`"
        buttonTextLeft="Confirm"
        buttonTextRight="Cancel"
        :isTextCenter="true"
        @clickButtonLeft="onClickUpdateStatusRequest('CLOSED')"
      >
      </molecule-modal-dialog>
      <!-- end modal close access -->
    </div>
    <!-- Modal End -->
  </div>
</template>

<script>
import {
  getSiteAccessById,
  updateSiteAccessById,
  deleteSiteAccessById,
  getVisitorsById,
  updateSiteAccessStatusById,
  addAttachmentById,
  editAttachmentById,
  deleteAttachmentById,
  resendEmailSiteAccess,
} from "@/api/module-1/site-access"
import { getSharedTags } from "@/api/api-module1.js"
import mixins from "@/mixins"
import mixinsDateTime from "@/mixins/mixinsDatetime"
import mixinsValidator from "@/mixins/mixinsValidator"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
import mixinsSiteHelper from "@/mixins/mixinsSiteHelper"
import { BBadge, BAlert } from "bootstrap-vue"
import AtomInput from "@/components/atoms/AtomInput.vue"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomDatepicker from "@/components/atoms/AtomDatepicker.vue"
import AtomTextArea from "@/components/atoms/AtomTextArea.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import MoleculeAttachmentList from "@/components/molecules/portal/idc/MoleculeAttachmentList.vue"
import MoleculeVisitorCardGroup from "@/components/molecules/portal/qrAccess/MoleculeVisitorCardGroup.vue"
import { IdTypeOption } from "@/constants"
import mixinsUser from "@/mixins/mixinsUser"
import { PermissionUser } from "@/constants/permission-user"
import { DateTime } from "luxon"

export default {
  name: "OrganismQrAccessDetail",
  components: {
    BBadge,
    AtomInput,
    AtomButton,
    AtomDatepicker,
    AtomTextArea,
    MoleculeModalDialog,
    MoleculeAttachmentList,
    MoleculeVisitorCardGroup,
    BAlert,
  },
  mixins: [
    mixins,
    mixinsDateTime,
    mixinsValidator,
    mixinsObjectHelper,
    mixinsSiteHelper,
    mixinsUser,
  ],
  data() {
    return {
      idTypeOptions: [
        {
          id: IdTypeOption.ID_CARD,
          name: "บัตรประจำตัวประชาชน",
        },
        {
          id: IdTypeOption.PASSPORT,
          name: "Passport",
        },
      ],

      siteAccessId: null,
      siteAccessStatus: null,

      customerDetail: {
        name: null,
        idcId: null,
        siteName: null,
      },

      isEditDetail: false,

      originRequestDetail: {
        startDate: null,
        endDate: null,
        description: null,
      },
      requestDetail: {
        startDate: null,
        endDate: null,
        description: null,
      },

      isApplyValidateRequestDetail: false,
      validateRequestDetailError: {
        startDate: false,
        endDate: false,
        description: false,
        visitor: false,
      },

      visitorList: [],

      attachmentListExternal: [],
      attachmentListInternal: [],
      tagList: [],
      visitorOptions: [],
      scrollToVisitorId: null,

      rejectReason: null,
    }
  },
  computed: {
    hasUpdateSiteAccessPermission() {
      return this.hasPermission(PermissionUser.WPM.SITE_ACCESS_WRITE)
    },
    hasDeleteSiteAccessPermission() {
      return this.hasPermission(PermissionUser.WPM.SITE_ACCESS_DELETE)
    },
    isInternalAccess() {
      return this.$route.meta.type === "internal"
    },

    isWaiting() {
      return this.siteAccessStatus === "WAITING"
    },
    isApproved() {
      return this.siteAccessStatus === "APPROVED"
    },
    isAccess() {
      return this.siteAccessStatus === "ACCESS"
    },
    isRejected() {
      return this.siteAccessStatus === "REJECTED"
    },
    isClosed() {
      return this.siteAccessStatus === "CLOSED"
    },

    isEditRequest() {
      return (
        this.isWaiting ||
        (this.isInternalAccess && !this.isRejected && !this.isClosed)
      )
    },

    isDeleteRequest() {
      return (
        this.isWaiting ||
        (this.isInternalAccess && (this.isAccess || this.isClosed))
      )
    },

    isCanCloseAccess() {
      return this.visitorList.every((visitor) => {
        if (!visitor.accessBuilding.length) return true

        return visitor.accessBuilding.every((building) => {
          if (!building.accessCard.length) return true

          return building.accessCard.every(
            (access) => access.checkInDate && access.checkOutDate
          )
        })
      })
    },

    isCanReAccess() {
      return this.isAfterNow(this.requestDetail.endDate)
    },

    badgeVariant() {
      if (this.siteAccessStatus === "WAITING") return "light-warning"
      if (this.siteAccessStatus === "APPROVED") return "light-success"
      if (this.siteAccessStatus === "ACCESS") return "light-primary"
      if (this.siteAccessStatus === "REJECTED") return "light-danger"
      return "light-secondary"
    },

    badgeStatus() {
      if (this.siteAccessStatus === "WAITING") return "Waiting"
      if (this.siteAccessStatus === "APPROVED") return "Approved"
      if (this.siteAccessStatus === "ACCESS") return "Access"
      if (this.siteAccessStatus === "REJECTED") return "Rejected"
      return "Closed"
    },

    startDate() {
      return this.setDateTimeFormat(
        this.requestDetail.startDate,
        "dd/MM/yyyy HH:mm"
      )
    },

    endDate() {
      return this.setDateTimeFormat(
        this.requestDetail.endDate,
        "dd/MM/yyyy HH:mm"
      )
    },

    isRequestDetailError() {
      return Object.values(this.validateRequestDetailError).some(
        (error) => error === true
      )
    },

    maxSiteAccessRequestEndDate() {
      if (!this.requestDetail.startDate) return null

      return DateTime.fromISO(this.requestDetail.startDate)
        .plus({ days: 30 })
        .set({ hour: 23, minute: 59, second: 59 })
        .toISO({ suppressMilliseconds: true })
    },

    minSiteAccessRequestStartDate() {
      return DateTime.now()
        .startOf("day")
        .set({ second: 0, millisecond: 0 })
        .toISO({ suppressMilliseconds: true })
    },
  },
  watch: {
    requestDetail: {
      deep: true,
      handler() {
        this.validateRequestDetail()
      },
    },
  },
  async mounted() {
    this.siteAccessId = this.$route?.params?.id.toString()
    await this.fetchData()
    await this.scrollToVisitorById()
  },
  methods: {
    async fetchData() {
      await this.getVisitorsById()
      await this.getSiteAccessById()
    },

    async getVisitorsById() {
      this.showLoading(true)
      await getVisitorsById(this.siteAccessId, true)
        .then((data) => {
          this.visitorOptions = data?.visitors.map((visitor) => ({
            id: visitor.accessVisitorId,
            name: `${visitor.firstname} ${visitor.lastname}`,
            latestBuildCard: visitor.latestAccessBuilding?.buildingCard
              ? {
                  buildingCard: visitor.latestAccessBuilding?.buildingCard,
                  checkInDate: visitor.latestAccessBuilding?.checkInDate
                    ? this.setDateTimeFormat(
                        visitor.latestAccessBuilding.checkInDate,
                        "dd/MM/yyyy HH:mm"
                      )
                    : null,
                  checkOutDate: visitor.latestAccessBuilding?.checkOutDate
                    ? this.setDateTimeFormat(
                        visitor.latestAccessBuilding.checkOutDate,
                        "dd/MM/yyyy HH:mm"
                      )
                    : null,
                }
              : null,
          }))
        })
        .finally(() => this.showLoading(false))
    },

    async getSiteAccessById() {
      this.showLoading(true)
      await getSiteAccessById(this.siteAccessId)
        .then(async (data) => {
          const siteName = await this.getSingleSiteNameBySiteId(data.siteId)
          
          this.customerDetail = {
            name: data.customerName,
            idcId: data.idcId.toString().padStart(8, "0"),
            siteName,
          }

          this.siteAccessStatus = data.status

          this.requestDetail = {
            startDate: data.requestStartDate,
            endDate: data.requestEndDate,
            description: data.description,
          }
          this.originRequestDetail = this.deepCopyObject(this.requestDetail)

          this.visitorList = data.visitors.map((visitor) => ({
            id: visitor.accessVisitorId,
            firstname: visitor.firstname,
            lastname: visitor.lastname,
            idType: this.idTypeOptions.find(
              (option) => option.id === visitor.idType
            ),
            idNumber: visitor.identity,
            email: visitor.email,
            phone: visitor.phone,
            selfCheckIn: {
              dateFirstCheckIn: visitor.selfCheckIn?.dateFirstCheckIn,
              dateLastCheckIn: visitor.selfCheckIn?.dateLastCheckIn,
              selfCheckInAt: visitor.selfCheckIn?.selfCheckInAt,
              name:
                !!visitor.selfCheckIn?.firstname &&
                !!visitor.selfCheckIn?.lastname
                  ? `${visitor.selfCheckIn?.firstname} ${visitor.selfCheckIn?.lastname}`
                  : null,
              idNumber: visitor.selfCheckIn?.identity,
              idExpiredDate: visitor.selfCheckIn?.expireDate,
              nationality: visitor.selfCheckIn?.nationality,
              address: visitor.selfCheckIn?.address,
              image: visitor.selfCheckIn?.image,
            },
            accessBuilding: visitor.accessBuildings.map((accessBuilding) => ({
              accessBuildingId: accessBuilding?.accessBuildingId,
              checkInDate: accessBuilding?.checkInDate,
              checkOutDate: accessBuilding?.checkOutDate,
              buildingCard: accessBuilding?.buildingCard,
              accessCard: accessBuilding?.accessCards.map((accessCard) => ({
                accessCardId: accessCard?.accessCardId,
                checkInDate: accessCard?.checkInDate,
                checkOutDate: accessCard?.checkOutDate,
                location: accessCard?.location,
                cardType: accessCard?.cardType,
                cardNumber: accessCard?.cardNumber,
              })),
            })),
          }))

          this.attachmentListExternal = data.attachment.external.map(
            (attachment) => ({
              attachmentId: attachment.attachmentId,
              filename: attachment.filename,
              filePath: attachment.filePath,
              name: attachment.title,
              description: attachment.description,
              tags: attachment.tags,
              createAt: attachment.createAt,
              updateAt: attachment.updateAt,
            })
          )
          this.attachmentListInternal = data.attachment.internal.map(
            (attachment) => ({
              attachmentId: attachment.attachmentId,
              filename: attachment.filename,
              filePath: attachment.filePath,
              name: attachment.title,
              description: attachment.description,
              tags: attachment.tags,
              createAt: attachment.createAt,
              updateAt: attachment.updateAt,
            })
          )
        })
        .catch(() => {
          this.toastError("Site Access Not Found")
          this.$router.push({ name: "internalSiteAccessManagement" })
        })
        .finally(() => this.showLoading(false))
      await this.getAttachmentTagOptionFromApi()
    },

    async getAttachmentTagOptionFromApi() {
      const tagFilter = {
        TagTypes: [2],
      }
      await getSharedTags(tagFilter, this.isInternalAccess).then((response) => {
        this.tagList = response.map((item) => item.key)
      })
    },

    async scrollToVisitorById() {
      const visitorId = this.$route.query.visitorId
      if (visitorId) {
        this.scrollToVisitorId = visitorId
      }
    },

    async onClickSaveDetail() {
      this.isApplyValidateRequestDetail = true
      this.validateRequestDetail()

      if (!this.isRequestDetailError) {
        this.isEditDetail = false
        const body = {
          requestStartDate: this.requestDetail.startDate,
          requestEndDate: this.requestDetail.endDate,
          description: this.requestDetail.description,
        }
        this.showLoading(true)
        await updateSiteAccessById(this.siteAccessId, body)
          .then(() => this.toastSuccess("Site Access updated successfully"))
          .catch(() => this.toastError("Site Access updated failed"))
          .finally(() => {
            this.showLoading(false)
            this.isApplyValidateRequestDetail = false
            this.fetchData()
          })
      }
    },

    onClickCancelSaveDetail() {
      this.requestDetail = this.deepCopyObject(this.originRequestDetail)
      this.isApplyValidateRequestDetail = false
      this.isEditDetail = false
    },

    onClickDeleteRequest() {
      this.openModal("modal-confirm-delete-site-access")
    },

    async onClickConfirmDeleteRequest() {
      this.showLoading(true)

      await deleteSiteAccessById(this.siteAccessId)
        .then(() => this.toastSuccess("Site Access deleted successfully"))
        .catch(() => this.toastError("Site Access deleted failed"))
        .finally(() => {
          this.closeModal()
          this.showLoading(false)
          this.$router.push({ name: "internalSiteAccessManagement" })
        })
    },

    // Visitor Start
    async onUpdateVisitorList(visitorList) {
      const updateVisitor = {
        visitor: visitorList.map((item) => {
          const visitor = {
            firstname: item.firstname,
            lastname: item.lastname,
            idType: item.idType.id,
            identity: item.idNumber,
            email: item.email,
            phone: item.phone,
            accessBuilding: item?.accessBuilding || [],
          }

          if (item.id.toString().includes("NEW_VISITOR")) {
            return visitor
          }

          return {
            accessVisitorId: item.id,
            ...visitor,
          }
        }),
      }

      this.showLoading(true)

      await updateSiteAccessById(this.siteAccessId, updateVisitor)
        .then(() => this.toastSuccess("Visitors updated successfully"))
        .catch(() => this.toastError("Visitors updated failed"))
        .finally(() => {
          this.fetchData()
          this.showLoading(false)
        })
    },
    // Visitor End

    onClickCopyVisitorListCreateNewSiteAccess() {
      this.$router.push({
        name: "internalCreateNewSiteAccess",
        params: {
          siteAccessId: this.siteAccessId,
        },
      })
    },

    // Attachment Start
    async onRequestAddAttachment(attachment) {
      let formData = new FormData()
      formData.append("Title", attachment.name)
      formData.append("Description", attachment.description)
      for (let tag of attachment.tags) {
        formData.append("Tags", tag)
      }
      formData.append("AttachmentType", attachment.type)
      for (let file of attachment.uploadFile) {
        formData.append("Files", file)
      }

      this.showLoading(true)
      await addAttachmentById(this.siteAccessId, formData)
        .then(() => this.toastSuccess("Site Access updated successfully"))
        .catch(() => this.toastError("Attachment uploaded failed. Please check the file size of your attachment and try again."))
        .finally(() => {
          this.fetchData()
          this.showLoading(false)
        })
    },

    async onRequestEditAttachment(attachment) {
      let formData = new FormData()
      formData.append("Title", attachment.name)
      formData.append("Description", attachment.description)
      for (let tag of attachment.tags) {
        formData.append("Tags", tag)
      }

      this.showLoading(true)
      await editAttachmentById(
        this.siteAccessId,
        attachment.attachmentId,
        formData
      )
        .then(() => this.toastSuccess("Site Access updated successfully"))
        .catch(() => this.toastError("Attachment updated failed. Please check the file size of your attachment and try again."))
        .finally(() => {
          this.fetchData()
          this.showLoading(false)
        })
    },

    async onRequestDeleteAttachment(attachment) {
      this.showLoading(true)
      await deleteAttachmentById(this.siteAccessId, attachment.attachmentId)
        .then(() => this.toastSuccess("Site Access updated successfully"))
        .catch(() => this.toastError("Site Access updated failed"))
        .finally(() => {
          this.fetchData()
          this.showLoading(false)
        })
    },

    // Attachment End

    // Update Status Start
    async onClickUpdateStatusRequest(status) {
      const body = {
        status,
        remark: status === "REJECTED" ? this.rejectReason : undefined,
      }
      this.showLoading(true)
      await updateSiteAccessStatusById(this.siteAccessId, body)
        .then(() =>
          this.toastSuccess("Site Access updated status successfully")
        )
        .catch(() => this.toastError("Site Access update status failed"))
        .finally(() => {
          this.closeModal()
          this.fetchData()
          this.showLoading(false)
        })
    },
    // Update Status End

    // Validate Start
    validateRequestDetail() {
      if (!this.isApplyValidateRequestDetail) {
        this.validateRequestDetailError = this.deepCopyObject({
          startDate: false,
          endDate: false,
          description: false,
          visitor: false,
        })
      } else {
        this.validateRequestDetailError = this.deepCopyObject({
          startDate: !this.requestDetail.startDate,
          endDate: !this.requestDetail.endDate,
          description: this.isEmptyString(this.requestDetail.description),
          visitor: this.visitorList.length === 0,
        })
      }
    },
    // Validate End

    async onResendEmail(visitorId) {
      this.showLoading(true)
      const body = {
        siteAccessId: this.siteAccessId,
        accessVisitorId: visitorId,
      }
      await resendEmailSiteAccess(body)
        .then(() => this.toastSuccess("Send email successfully"))
        .catch(() => this.toastError("Send email failed"))
        .finally(this.showLoading(false))
    },

    // Modal Start
    openModal(modalId) {
      this.modalId = modalId
      this.$bvModal.show(this.modalId)
    },

    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = null
    },
    // Modal End
  },
}
</script>

<style lang="scss" scoped>
hr {
  width: 100%;
  stroke-width: 1px;
  stroke: #e9ecef;
}

.card__container {
  width: 100%;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.06);
}

.header__container {
  padding: 21px;
}

.text__header {
  color: #5e5873;
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 35px;
}

.text__customer-detail_topic {
  font-size: 14px;
  font-weight: 600;
  color: #6e6b7b;
  width: 130px;
}

.text__customer-detail_data {
  font-size: 14px;
  font-weight: 400;
  color: #5e5873;
}

.text__normal {
  color: #6e6b7b;
  font-size: 14px;
  font-weight: 400;
}

.text__medium {
  color: #6e6b7b;
  font-size: 14px;
  font-weight: 500;
}

.badge--style {
  max-width: 30ch;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tag--space {
  margin-left: 4px;
  margin-bottom: 4px;
}

.mt-30px {
  margin-top: 30px;
}

.ml-12px {
  margin-left: 12px;
}

.w-150px {
  width: 150px;
}

.w-des {
  width: calc(100% - 150px);
}

.w-115px {
  width: 115px;
}

.btn-edit-request ::v-deep .padding-icon {
  padding-top: 0;
  padding-right: 0;
}
</style>
